import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, Button, Modal, TextField, MenuItem, Alert, IconButton, InputAdornment} from '@mui/material'
import {Visibility, VisibilityOff} from '@mui/icons-material'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {adminApi} from '../../../../services'
import {parseError} from '../../../../utils/helpers'

// The two environments HHAeXchange issues credentials for. They are separate
// enrolments with separate secrets, not one credential re-scoped, so the choice
// belongs next to the secret rather than buried in settings.
export const ENVIRONMENTS = [
    {value: 'https://implementation.hhaexchange.com', label: 'Implementation (sandbox)'},
    {value: 'https://cloud.hhaexchange.com', label: 'Production'},
]

export default ({open, setOpen, organization, onSaved}) => {
    const {setLoading, setMessageBox} = useGlobalStates()
    const credential = organization?.hhax?.credential
    const configured = Boolean(credential?.configured) && !credential?.using_default

    const [clientId, setClientId] = useState('')
    const [clientSecret, setClientSecret] = useState('')
    const [baseUrl, setBaseUrl] = useState(ENVIRONMENTS[0].value)
    const [apiVersion, setApiVersion] = useState('v2')
    const [showSecret, setShowSecret] = useState(false)
    const [problem, setProblem] = useState(null)

    // The secret is never returned by the API, so the field always opens empty —
    // even when a credential is stored. Only the client id and environment can
    // be prefilled, and rotating means typing the new secret in full.
    useEffect(() => {
        if (!open) return
        setClientId(credential?.client_id || '')
        setClientSecret('')
        setBaseUrl(credential?.base_url || ENVIRONMENTS[0].value)
        setApiVersion(credential?.api_version || 'v2')
        setShowSecret(false)
        setProblem(null)
    }, [open])

    const clientIdChanged = configured && credential?.client_id && clientId.trim() !== credential.client_id

    const save = () => {
        if (!clientId.trim() || !clientSecret.trim()) {
            setProblem('Both the client ID and client secret are required.')
            return
        }
        setProblem(null)
        setLoading(true)
        adminApi
            .setHhaxCredentials(organization?.id, {
                client_id: clientId.trim(),
                client_secret: clientSecret.trim(),
                base_url: baseUrl,
                api_version: apiVersion,
            })
            .then(({data}) => {
                setLoading(false)
                setOpen(false)
                // Saving and verifying are different outcomes: HHAeXchange being
                // unreachable does not stop the credential being stored, and an
                // operator told only "saved" would believe it had been checked.
                const warning = data?.data?.warning
                setMessageBox(
                    warning
                        ? {status: 'warning', title: 'Credentials saved, but not verified', description: warning}
                        : {
                              status: 'success',
                              title: 'Credentials saved',
                              description: 'HHAeXchange accepted these credentials.',
                          },
                )
                onSaved?.()
            })
            .catch(error => {
                setLoading(false)
                const {title, description} = parseError(error)
                // Rejected credentials and a missing encryption key are both
                // fixable right here, so they stay in the dialog with the typed
                // values rather than closing it and losing them.
                const status = error?.response?.status
                if (status === 422 || status === 503) {
                    setProblem(description || title)
                    return
                }
                setOpen(false)
                setMessageBox({
                    status: 'error',
                    title: title || 'Could not save credentials',
                    description: description || 'There was an error saving these credentials. Please try again later.',
                })
            })
    }

    return (
        <Modal open={open}>
            <div className="container">
                <div className="content">
                    <div className="title">
                        <Typography variant="h6" color="text.primary">
                            {configured ? 'Rotate HHAeXchange credentials' : 'Set HHAeXchange credentials'}
                        </Typography>
                        <br />
                        <Typography variant="body2" color="text.secondary">
                            {organization?.name} submits EVV visits under these credentials. They are stored encrypted and never shown again.
                        </Typography>
                    </div>
                    <br />
                    {problem && (
                        <>
                            <Alert severity="error">{problem}</Alert>
                            <br />
                        </>
                    )}
                    <div className="field">
                        <Typography variant="h8" color="text.primary">
                            Client ID
                        </Typography>
                        <TextField
                            placeholder="Client ID issued by HHAeXchange"
                            fullWidth
                            value={clientId}
                            onChange={e => setClientId(e.target.value)}
                        />
                    </div>
                    <div className="field">
                        <Typography variant="h8" color="text.primary">
                            Client Secret
                        </Typography>
                        <TextField
                            placeholder={configured ? 'Enter the new secret' : 'Client secret issued by HHAeXchange'}
                            fullWidth
                            type={showSecret ? 'text' : 'password'}
                            value={clientSecret}
                            onChange={e => setClientSecret(e.target.value)}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowSecret(!showSecret)} edge="end">
                                                {showSecret ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </div>
                    <div className="field">
                        <Typography variant="h8" color="text.primary">
                            Environment
                        </Typography>
                        <TextField select fullWidth value={baseUrl} onChange={e => setBaseUrl(e.target.value)}>
                            {ENVIRONMENTS.map(env => (
                                <MenuItem key={env.value} value={env.value}>
                                    {env.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </div>
                    <div className="field">
                        <Typography variant="h8" color="text.primary">
                            API Version
                        </Typography>
                        <TextField select fullWidth value={apiVersion} onChange={e => setApiVersion(e.target.value)}>
                            <MenuItem value="v2">v2</MenuItem>
                            <MenuItem value="v1">v1</MenuItem>
                        </TextField>
                    </div>
                    {clientIdChanged && (
                        <>
                            <Alert severity="warning">
                                HHAeXchange requires a visit to be updated with the client ID that created it. Changing the client ID means visits
                                already submitted under the old one can no longer be corrected or deleted.
                            </Alert>
                            <br />
                        </>
                    )}
                    <Typography variant="body2" color="text.secondary">
                        These credentials are checked against HHAeXchange before they are saved.
                    </Typography>
                    <br />
                    <div className="actions">
                        <Button variant="outlined" fullWidth onClick={() => setOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="contained" fullWidth onClick={save}>
                            {configured ? 'Rotate' : 'Save'}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    )
}
